import React from "react";
import { useTranslation } from "react-i18next";
import CardDesignEditor from "components/common/branding/CardDesignEditor";
import type {
  CardDesignPreview,
  CardFaceOption,
} from "components/common/branding/CardDesignEditor";
import { effectiveCard } from "components/common/branding/cardDesign";
import {
  CARD_FACES,
  platformDesign,
} from "components/common/cards/CompanyPaymentCard";
import type {
  CardFace,
  CardFills,
} from "components/common/cards/CompanyPaymentCard";

const FACE_LABELS: Record<CardFace, string> = {
  stock: "Card artwork",
  brand: "Primary color",
  background: "Background color",
};

interface PlatformCardDesignProps {
  /** The platform's own card artwork, absent until it uploads one. */
  url?: string;
  uploading: boolean;
  /** Resolves true once the artwork is stored. */
  onUpload: (file: File) => Promise<boolean>;
  onRemove: () => Promise<void> | void;
  /** The draft card keys; empty where the record has none. */
  face: string;
  textMode: string;
  /** The older artwork rule and its colour, which still decide the card until a face is saved. */
  art: string;
  background: string;
  /** The draft's colour-face fills. */
  fills: CardFills;
  onFaceChange: (face: CardFace) => void;
  onTextModeChange: (mode: "light" | "dark") => void;
}

/**
 * The platform's Card tab: the card every company wears until it sets its own.
 * The platform's artwork is the whole design rather than a layer, so a colour
 * face replaces it instead of sitting under it.
 */
const PlatformCardDesign: React.FC<PlatformCardDesignProps> = ({
  url,
  uploading,
  onUpload,
  onRemove,
  face,
  textMode,
  art,
  background,
  fills,
  onFaceChange,
  onTextModeChange,
}) => {
  const { t } = useTranslation();
  // The platform is the top of the chain: nothing to inherit, and no company upload of its own.
  const { faceIsChosen, artMode, shownFace, isLightText } = effectiveCard(
    {
      paymentCardFace: face,
      paymentCardTextMode: textMode,
      paymentCardArt: art,
      paymentCardBackground: background,
    },
    {},
    false,
  );
  const showsArtwork = shownFace === "stock";

  // An older "own" or "none" rule leaves no artwork at this level, so it wears the background or the gradient.
  const preview: CardDesignPreview = showsArtwork
    ? url
      ? { backgroundImage: url, textMode }
      : { face: "stock", art: "none", textMode }
    : faceIsChosen
      ? { face: shownFace, art: "none", fills, textMode }
      : {
          face: "",
          art: "none",
          backgroundColor: background || undefined,
          textMode,
        };

  const faceOptions: CardFaceOption[] = CARD_FACES.map((option) => ({
    face: option,
    label: FACE_LABELS[option],
    swatch:
      option === "stock"
        ? {
            backgroundImage: platformDesign(url ?? ""),
            backgroundSize: "cover",
            backgroundPosition: "center",
          }
        : { backgroundColor: fills[option] },
  }));

  return (
    <CardDesignEditor
      title={t("Card design")}
      help={t("PlatformCardArtworkHelp")}
      preview={preview}
      faceOptions={faceOptions}
      shownFace={shownFace}
      onFaceChange={onFaceChange}
      inkIsAutomatic={
        faceIsChosen
          ? shownFace !== "stock"
          : artMode !== "default" && Boolean(background)
      }
      isLightText={isLightText}
      onTextModeChange={onTextModeChange}
      artworkTitle={t("Card artwork")}
      artworkHelp={t("PlatformCardArtworkAccountWins")}
      hasArtwork={Boolean(url)}
      uploading={uploading}
      onUpload={async (file) => {
        // The face choice is locked while this runs, so the face it started on is still the one shown.
        if ((await onUpload(file)) && !showsArtwork) onFaceChange("stock");
      }}
      onRemove={onRemove}
    />
  );
};

export default PlatformCardDesign;
